import type {
  CogniteClient,
  DatapointAggregate,
  DatapointAggregates,
  Datapoints,
  DatapointsMultiQuery,
  DatapointsQuery,
  DatapointsQueryInstanceId,
  DoubleDatapoint,
  DoubleDatapoints,
  StringDatapoint,
  StringDatapoints,
} from '@cognite/sdk';

import { filterDatapointStrings } from './utils';

type RetrieveDatapointsFn = (
  query: DatapointsMultiQuery
) => Promise<DoubleDatapoints[] | DatapointAggregates[]>;

export type CogniteClientDependencies = {
  datapoints: Pick<CogniteClient['datapoints'], 'retrieveLatest' | 'retrieve'>;
  project: string;
  post: CogniteClient['post'];
};

/*
   DatapointService
   A temporary service to support instance ID, once the support is added to the SDK this service will be removed.
   TODO(FUS-000): INFIELD-2624 Remove this service once the SDK supports instance ID. 
   
   TODO(FO-3044): The datapoint service is initialized directly in some components and timeseries service.
   If a CDM duplicate is created, it needs to be updated in multiple places.
*/
export class DatapointService {
  client;

  constructor(client: CogniteClientDependencies) {
    this.client = client;
  }

  async retrieveLatest(externalIds: string[], timeseriesSpace: string) {
    const {
      data: { items },
    } = await this.client.post<{ items: Datapoints[] }>(
      `api/v1/projects/${this.client.project}/timeseries/data/latest`,
      {
        data: {
          items: externalIds.map((externalId) => ({
            instanceId: {
              externalId,
              space: timeseriesSpace,
            },
          })),
        },
      }
    );
    return items;
  }

  retrieve(isIdm: boolean): RetrieveDatapointsFn {
    const { client } = this;

    if (!isIdm) {
      return (query: DatapointsMultiQuery) =>
        new Promise((resolve, reject) => {
          client.datapoints
            .retrieve(query)
            .then((data) => {
              resolve(formatDatapointsTimestamp(data));
            })
            .catch(reject);
        });
    }

    // API doc reference: https://api-docs.cognite.com/20230101/tag/Time-series/operation/getMultiTimeSeriesDatapoints
    // "Array of DatapointsGetAggregateDatapoint (object) or
    // (DatapointsGetDatapoint (DatapointsGetDoubleDatapoint (object) or DatapointsGetStringDatapoint (object)))"
    return (query: DatapointsMultiQuery) =>
      new Promise((resolve, reject) => {
        client
          .post<{ items: Datapoints[] | DatapointAggregates[] }>(
            `api/v1/projects/${this.client.project}/timeseries/data/list`,
            {
              data: {
                ...query,
                items: query.items.map((item) => {
                  if (isInstanceIdQuery(item)) {
                    return {
                      instanceId: item.instanceId,
                    };
                  }
                  const itemKeys = Object.keys(item).join(', ');
                  throw new Error(
                    `IDM mode requires instanceId in query items, but received: { ${itemKeys} }`
                  );
                }),
              },
            }
          )
          .then(({ data: { items } }) => {
            resolve(formatDatapointsTimestamp(items));
          })
          .catch(reject);
      });
  }
}

const isInstanceIdQuery = (
  item: DatapointsQuery
): item is DatapointsQueryInstanceId =>
  typeof item === 'object' && 'instanceId' in item;

const isDoubleDatapoint = (
  datapoint: DoubleDatapoint | DatapointAggregate | StringDatapoints
): datapoint is DoubleDatapoint => {
  return 'value' in datapoint && typeof datapoint.value === 'number';
};

const isStringDatapoint = (
  datapoint: DoubleDatapoint | DatapointAggregate | StringDatapoint
): datapoint is StringDatapoint => {
  return 'value' in datapoint && typeof datapoint.value === 'string';
};

const isDatapointAggregate = (
  datapoint: DoubleDatapoint | DatapointAggregate | StringDatapoint
): datapoint is DatapointAggregate => {
  return !isDoubleDatapoint(datapoint) && !isStringDatapoint(datapoint);
};

/**
 * Type guard to check if data is DoubleDatapoints.
 * DoubleDatapoints has isString: false and datapoints with 'value' property.
 */
const isDoubleDatapoints = (
  data: Datapoints | DatapointAggregates
): data is DoubleDatapoints => {
  if (data.isString !== false) return false;
  // Check first datapoint - DoubleDatapoint has 'value', DatapointAggregate has 'count'/'average'/etc
  const firstDp = data.datapoints[0];
  if (!firstDp) return true; // Empty array, default to DoubleDatapoints
  return isDoubleDatapoint(firstDp);
};

/**
 * Type guard to check if data is DatapointAggregates.
 * DatapointAggregates has isString: false and datapoints with aggregate properties.
 */
const isDatapointAggregates = (
  data: Datapoints | DatapointAggregates
): data is DatapointAggregates => {
  if (data.isString !== false) return false;
  // Check first datapoint for aggregate properties
  const firstDp = data.datapoints[0];
  if (!firstDp) return false; // Empty array, default to not being aggregates
  return isDatapointAggregate(firstDp);
};

/**
 * Type guard to check if an array contains only DoubleDatapoints.
 * Checks the first item to determine array type (API returns consistent types).
 */
const isDoubleDatapointsArray = (
  data: Datapoints[] | DatapointAggregates[]
): data is DoubleDatapoints[] => {
  const firstItem = data[0];
  if (!firstItem) return true; // Empty array, default to DoubleDatapoints
  return isDoubleDatapoints(firstItem);
};

/**
 * Type guard to check if an array contains only DatapointAggregates.
 * Checks the first item to determine array type (API returns consistent types).
 */
const isDatapointAggregatesArray = (
  data: Datapoints[] | DatapointAggregates[]
): data is DatapointAggregates[] => {
  const firstItem = data[0];
  if (!firstItem) return false; // Empty array, default to not aggregates
  return isDatapointAggregates(firstItem);
};

const formatDoubleDatapointsTimestamp = (
  datapoints: DoubleDatapoints
): DoubleDatapoints => {
  return {
    ...datapoints,
    datapoints: datapoints.datapoints.map((dp) => ({
      ...dp,
      timestamp: new Date(dp.timestamp),
    })),
  };
};

const formatDatapointAggregatesTimestamp = (
  datapoints: DatapointAggregates
): DatapointAggregates => {
  return {
    ...datapoints,
    datapoints: datapoints.datapoints.map((dp) => ({
      ...dp,
      timestamp: new Date(dp.timestamp),
    })),
  };
};

const formatDatapointsTimestamp = (
  datapoints: Datapoints[] | DatapointAggregates[]
): DoubleDatapoints[] | DatapointAggregates[] => {
  if (isDatapointAggregatesArray(datapoints)) {
    return datapoints.map(formatDatapointAggregatesTimestamp);
  }
  const filteredDatapoints = filterDatapointStrings(datapoints);
  if (isDoubleDatapointsArray(filteredDatapoints)) {
    return filteredDatapoints.map(formatDoubleDatapointsTimestamp);
  }
  throw new Error(
    'Invalid data type received when retrieving datapoints. Expected DoubleDatapoints or DatapointAggregates.'
  );
};
